import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import HayatLogo from "../../assets/images/AlHayatLogo.svg";
import loginLeafMark from "../../assets/images/loginLeafMark.png";
import loginService from "../../services/LoginServices";
import { showToast } from "../component/common/toastNotification";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loginDetails } from "../../globalState/actions/authAction/authAction";
import { useAppDispatch } from "../../globalState/hooks";
import LoginCopyright from "../../components/LoginCopyright";
import SupportContactLinks from "../../components/SupportContactLinks";
import { applyDocumentDirection } from "../../helper/documentDirection";
import { NJIM } from "../../theme/njimTheme";

const BRAND_INK = NJIM.ink;
const BRAND_MINT = NJIM.mintLight;
const SURFACE_APP = NJIM.muted;
const SURFACE_SUNKEN = NJIM.muted;
const TEXT_PRIMARY = NJIM.textPrimary;
const TEXT_SECONDARY = NJIM.textSecondary;

const authTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "10px",
    backgroundColor: SURFACE_SUNKEN,
    fontSize: 13.5,
    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    "& fieldset": {
      border: `1px solid ${NJIM.border}`,
      borderRadius: "10px",
    },
    "&:hover fieldset": {
      borderColor: NJIM.border,
    },
    "&.Mui-focused": {
      backgroundColor: NJIM.white,
      boxShadow: "0 0 0 3px rgba(22,45,58,0.06)",
      "& fieldset": {
        borderColor: NJIM.textMuted,
        borderWidth: "1px",
      },
    },
  },
  "& .MuiOutlinedInput-input": {
    py: 1.25,
    color: TEXT_PRIMARY,
    "&::placeholder": {
      color: TEXT_SECONDARY,
      opacity: 1,
    },
  },
};

const GhostLeaf = ({
  width,
  height,
  bottom,
  top,
  insetInlineEnd,
  insetInlineStart,
  opacity,
  rotate,
}: {
  width: number;
  height: number;
  bottom?: number;
  top?: number;
  insetInlineEnd?: number;
  insetInlineStart?: number;
  opacity: number;
  rotate: number;
}) => (
  <Box
    component="img"
    src={loginLeafMark}
    alt=""
    aria-hidden
    sx={{
      position: "absolute",
      width,
      height,
      bottom,
      top,
      ...(insetInlineEnd !== undefined ? { insetInlineEnd } : {}),
      ...(insetInlineStart !== undefined ? { insetInlineStart } : {}),
      opacity,
      transform: `rotate(${rotate}deg)`,
      filter: "grayscale(1) invert(1)",
      mixBlendMode: "screen",
      objectFit: "contain",
      pointerEvents: "none",
      userSelect: "none",
    }}
  />
);

const ForceChangePassword: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLang, setSelectedLang] = useState(i18n.language || "en");

  const getInitialLanguage = useCallback((): string => {
    const savedLang = localStorage.getItem("i18nextLng");
    return savedLang && (savedLang === "en" || savedLang === "ar")
      ? savedLang
      : i18n.language || "en";
  }, [i18n.language]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const tempPassword = sessionStorage.getItem("tempPassword");
    if (!token || !tempPassword) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const savedLang = getInitialLanguage();
    if (savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
    applyDocumentDirection(savedLang);
    setSelectedLang(savedLang);
  }, [getInitialLanguage, i18n]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, []);

  const passwordStrength = (pw: string): number => {
    let score = 0;
    if (pw.length >= 6) score += 25;
    if (pw.length >= 10) score += 25;
    if (/[A-Z]/.test(pw)) score += 25;
    if (/[0-9!@#$%^&*]/.test(pw)) score += 25;
    return score;
  };

  const strength = passwordStrength(newPassword);
  const strengthColor =
    strength <= 25 ? NJIM.danger : strength <= 50 ? NJIM.warning : strength <= 75 ? NJIM.warning : NJIM.mint;
  const strengthLabel =
    strength <= 25
      ? t("changePassword.weak", { defaultValue: "Weak" })
      : strength <= 50
        ? t("changePassword.fair", { defaultValue: "Fair" })
        : strength <= 75
          ? t("changePassword.good", { defaultValue: "Good" })
          : t("changePassword.strong", { defaultValue: "Strong" });

  const getRoleRedirect = () => {
    const role = (sessionStorage.getItem("tempRole") ?? "").toLowerCase();
    if (role === "admin") return "/CustomerEnrolment";
    if (role === "supervisor") return "/workflowPortal";
    if (role === "whm") return "/itemControlRelease";
    if (role === "salesrep") return "/codSalesRep";
    if (role === "salesmanager") return "/codSalesManager";
    return "/dashboard";
  };

  const changeLanguage = (lang: string) => {
    setSelectedLang(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem("i18nextLng", lang);
    applyDocumentDirection(lang);
    setAnchorEl(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      showToast(
        t("changePassword.tooShort", {
          defaultValue: "Password must be at least 6 characters",
        }),
        "error"
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast(
        t("changePassword.mismatch", { defaultValue: "Passwords do not match" }),
        "error"
      );
      return;
    }

    const tempPassword = sessionStorage.getItem("tempPassword") ?? "";
    if (!tempPassword) {
      showToast(
        t("changePassword.sessionExpired", {
          defaultValue: "Session expired. Please log in again.",
        }),
        "error"
      );
      navigate("/", { replace: true });
      return;
    }

    try {
      setLoading(true);
      await loginService.changeOwnPassword(tempPassword, newPassword);

      const pendingLoginRaw = sessionStorage.getItem("pendingLogin");
      if (pendingLoginRaw) {
        dispatch(loginDetails(JSON.parse(pendingLoginRaw)));
      }
      sessionStorage.removeItem("tempPassword");
      sessionStorage.removeItem("tempRole");
      sessionStorage.removeItem("pendingLogin");

      setDone(true);
      showToast(
        t("changePassword.success", {
          defaultValue: "Password changed successfully!",
        }),
        "success"
      );
      setTimeout(() => {
        navigate(getRoleRedirect(), { replace: true });
      }, 1800);
    } catch {
      showToast(
        t("changePassword.failed", {
          defaultValue: "Failed to change password. Please try again.",
        }),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <HubOutlinedIcon sx={{ fontSize: 18, color: BRAND_MINT }} />,
      label: t("login.featureTraceability"),
    },
    {
      icon: <SyncOutlinedIcon sx={{ fontSize: 18, color: BRAND_MINT }} />,
      label: t("login.featureSageIntegration"),
    },
    {
      icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 18, color: BRAND_MINT }} />,
      label: t("login.featureCodApprovals"),
    },
  ];

  const languages = [
    { value: "en", label: "GB English" },
    { value: "ar", label: "SA (Arabic) العربية" },
  ];

  const passwordField = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    show: boolean,
    onToggleShow: () => void,
    error?: string
  ) => (
    <Box sx={{ mb: error ? 1 : 2 }}>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: 13.5,
          color: TEXT_PRIMARY,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <TextField
        fullWidth
        type={show ? "text" : "password"}
        placeholder="••••••••"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        error={!!error}
        sx={authTextFieldSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ marginInlineEnd: 0.75 }}>
              <LockOutlinedIcon sx={{ color: TEXT_SECONDARY, fontSize: 16 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={onToggleShow}
                edge="end"
                size="small"
              >
                {show ? (
                  <VisibilityIcon sx={{ fontSize: 20, color: TEXT_SECONDARY }} />
                ) : (
                  <VisibilityOffIcon sx={{ fontSize: 20, color: TEXT_SECONDARY }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {error && (
        <Typography sx={{ color: NJIM.danger, fontSize: 12, mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100dvh",
        maxHeight: "100dvh",
        overflow: "hidden",
        bgcolor: NJIM.white,
      }}
    >
      <Box
        sx={{
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
          display: { xs: "flex", lg: "grid" },
          flexDirection: { xs: "column" },
          gridTemplateColumns: { lg: "1fr 1fr" },
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {/* Hero panel */}
        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            flexDirection: "column",
            position: "relative",
            overflow: "hidden",
            color: NJIM.white,
            bgcolor: BRAND_INK,
            m: 2,
            borderRadius: "20px",
            height: "calc(100% - 32px)",
            maxHeight: "calc(100% - 32px)",
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              "@keyframes njimPulse1": {
                "0%, 100%": { opacity: 0.15, transform: "scale(1)" },
                "50%": { opacity: 0.45, transform: "scale(1.18)" },
              },
              "@keyframes njimPulse2": {
                "0%, 100%": { opacity: 0.12, transform: "scale(1)" },
                "50%": { opacity: 0.38, transform: "scale(1.15)" },
              },
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 420,
                height: 420,
                top: -140,
                right: isRTL ? "auto" : -100,
                left: isRTL ? -100 : "auto",
                borderRadius: "50%",
                bgcolor: NJIM.mint,
                filter: "blur(90px)",
                animation: "njimPulse1 5s ease-in-out infinite",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                width: 460,
                height: 460,
                bottom: -160,
                left: isRTL ? "auto" : -130,
                right: isRTL ? -130 : "auto",
                borderRadius: "50%",
                bgcolor: NJIM.mint,
                filter: "blur(100px)",
                animation: "njimPulse2 6s ease-in-out infinite 1.5s",
              }}
            />
            <GhostLeaf
              width={860}
              height={860}
              bottom={-220}
              insetInlineEnd={-200}
              opacity={0.28}
              rotate={-10}
            />
            <GhostLeaf
              width={480}
              height={480}
              top={-130}
              insetInlineStart={-140}
              opacity={0.13}
              rotate={16}
            />
          </Box>

          <Box
            sx={{
              position: "relative",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              px: "44px",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 400,
                mx: "auto",
                borderRadius: "22px",
                bgcolor: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.09)",
                backdropFilter: "blur(22px)",
                WebkitBackdropFilter: "blur(22px)",
                p: "38px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.55)",
                  mb: 2,
                }}
              >
                {t("login.njim")}
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontSize: { lg: "2.15rem" },
                  fontWeight: 500,
                  lineHeight: 1.25,
                  color: NJIM.white,
                }}
              >
                {t("changePassword.title", { defaultValue: "Change Your Password" })}
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    mt: 1,
                    fontSize: 16,
                    fontWeight: 400,
                    lineHeight: 1.625,
                    color: "rgba(255,255,255,0.55)",
                  }}
                >
                  {t("changePassword.subtitle", {
                    defaultValue:
                      "Your admin has reset your password. Please set a new one to continue.",
                  })}
                </Box>
              </Typography>
              <Box component="ul" sx={{ mt: 4.5, p: 0, m: 0, listStyle: "none" }}>
                {features.map((feature) => (
                  <Box
                    component="li"
                    key={feature.label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.75,
                      mb: 2,
                      "&:last-of-type": { mb: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: "rgba(255,255,255,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "rgba(255,255,255,0.85)",
                        lineHeight: 1.4,
                      }}
                    >
                      {feature.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <LoginCopyright
            sx={{
              position: "relative",
              px: "44px",
              pb: 4,
              fontSize: 12,
              color: "rgba(255,255,255,0.4)",
            }}
          />
        </Box>

        {/* Form panel */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            height: { lg: "100%" },
            maxHeight: "100%",
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: SURFACE_APP,
            p: { xs: 2.5, lg: 5 },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 24,
              right: isRTL ? "auto" : 24,
              left: isRTL ? 24 : "auto",
              zIndex: 10,
            }}
          >
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                border: `1px solid ${NJIM.border}`,
                bgcolor: NJIM.white,
                boxShadow: "0 1px 2px rgba(23,34,55,0.04)",
                "&:hover": { bgcolor: NJIM.white },
              }}
              aria-label="Change language"
            >
              <TranslateOutlinedIcon sx={{ fontSize: 20, color: TEXT_SECONDARY }} />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 180,
                  borderRadius: "10px",
                  border: `1px solid ${NJIM.border}`,
                  boxShadow: "0 4px 16px rgba(23,34,55,0.05)",
                },
              }}
            >
              {languages.map((lang) => (
                <MenuItem
                  key={lang.value}
                  onClick={() => changeLanguage(lang.value)}
                  selected={selectedLang === lang.value}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography sx={{ fontSize: 13 }}>{lang.label}</Typography>
                    {selectedLang === lang.value && (
                      <CheckIcon sx={{ fontSize: 18, color: BRAND_INK }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: { xs: 1, lg: 2 },
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 420 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: { xs: 2, lg: 2.5 },
                }}
              >
                <img
                  src={HayatLogo}
                  alt="Al Hayat Scientific Office"
                  style={{
                    height: "clamp(48px, 8vh, 80px)",
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Card
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: `1px solid ${NJIM.border}`,
                  boxShadow:
                    "0 1px 2px rgba(23,34,55,0.04), 0 4px 16px rgba(23,34,55,0.05)",
                  bgcolor: NJIM.white,
                  p: { xs: 3, sm: 4 },
                }}
              >
                {done ? (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <CheckCircleIcon sx={{ fontSize: 56, color: NJIM.mint, mb: 1.5 }} />
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        mb: 0.75,
                      }}
                    >
                      {t("changePassword.success", {
                        defaultValue: "Password changed successfully!",
                      })}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
                      {t("changePassword.redirecting", {
                        defaultValue: "Redirecting you now…",
                      })}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          bgcolor: SURFACE_SUNKEN,
                          border: `1px solid ${NJIM.border}`,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <LockResetIcon sx={{ fontSize: 22, color: BRAND_INK }} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 24,
                            fontWeight: 600,
                            color: TEXT_PRIMARY,
                            lineHeight: 1.2,
                          }}
                        >
                          {t("changePassword.title", {
                            defaultValue: "Change Your Password",
                          })}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.5,
                            fontSize: 14,
                            color: TEXT_SECONDARY,
                            lineHeight: 1.5,
                          }}
                        >
                          {t("changePassword.subtitle", {
                            defaultValue:
                              "Your admin has reset your password. Please set a new one to continue.",
                          })}
                        </Typography>
                      </Box>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit}>
                      {passwordField(
                        t("changePassword.newPassword", { defaultValue: "New Password" }),
                        newPassword,
                        setNewPassword,
                        showNew,
                        () => setShowNew((prev) => !prev)
                      )}

                      {newPassword.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <LinearProgress
                            variant="determinate"
                            value={strength}
                            sx={{
                              height: 5,
                              borderRadius: 3,
                              backgroundColor: SURFACE_SUNKEN,
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: strengthColor,
                                borderRadius: 3,
                              },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: strengthColor,
                              mt: 0.5,
                              fontWeight: 600,
                            }}
                          >
                            {strengthLabel}
                          </Typography>
                        </Box>
                      )}

                      {passwordField(
                        t("changePassword.confirmPassword", {
                          defaultValue: "Confirm New Password",
                        }),
                        confirmPassword,
                        setConfirmPassword,
                        showConfirm,
                        () => setShowConfirm((prev) => !prev),
                        confirmPassword.length > 0 && newPassword !== confirmPassword
                          ? t("changePassword.mismatch", {
                              defaultValue: "Passwords do not match",
                            })
                          : undefined
                      )}

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={
                          loading ||
                          !newPassword ||
                          !confirmPassword ||
                          newPassword !== confirmPassword
                        }
                        className="njim-primary-btn"
                        sx={{
                          mt: 1,
                          py: 1.25,
                          minHeight: 40,
                          fontSize: 13,
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={22} sx={{ color: NJIM.white }} />
                        ) : (
                          t("changePassword.submit", { defaultValue: "Set New Password" })
                        )}
                      </Button>
                    </Box>
                  </>
                )}
              </Card>

              <Box sx={{ mt: { xs: 2.5, lg: 3 } }}>
                <SupportContactLinks variant="login" />
              </Box>

              <LoginCopyright
                sx={{
                  fontSize: 12,
                  color: TEXT_SECONDARY,
                  textAlign: "center",
                  mt: { xs: 1.5, lg: 2 },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ForceChangePassword;
